<template>
  <div class="list" :class="{'isEng' : $isStories}">
    <div class="is-flex project-button-container">
      <sort class="sort" v-if="!isStory && !fromTemplate && !isEditProjectInstructions" :showCreate="showCreate"></sort>
      <a v-if="editPostInstructionsPermission" class="button is-primary has-text-weight-bold has-text-white is-uppercase edit-instructions-button"
         tabindex="0"
         :disabled="disableSaveButton"
         @keypress.enter.space="$refs.projectInstructions.toggleEditInstructions()"
         @click="$refs.projectInstructions.toggleEditInstructions()">
         {{isEditProjectInstructions ? $t('projectPage.save') : $t('projectPage.editInstructions')}}
      </a>
    </div>
    <project-instructions v-if="viewProjectInstructionPermission" ref="projectInstructions"
                          @onSetEditInstructions="onSetEditInstructions"
                          @onSetDisableSaveButton="onSetDisableSaveButton"></project-instructions>
    <infinite-scroll :callback="fetch">
      <transition-group :name="transitionName">
        <post v-for="post in posts" ref="postRef"
              :key="post.id"
              :post="post"
              :isSnapshot="isSnapshot"
              @showPostGridParent="showPostGrid"></post>
      </transition-group>
      <loader :loading="postLoading" />
    </infinite-scroll>
    <grid-modal :postID="selectedPostID" :selectedCommentID="selectedCommentID" v-if="showModal" :scrollToComment="this.scrollToComment"> </grid-modal>
  </div>
</template>

<script>
import componentState from '@/mixins/component-state'
import InfiniteScroll from '@/components/Controls/InfiniteScroll'
import Loader from '@/components/Base/Loader/loader'
import auth from '@/mixins/auth'
import socket from '@/libs/socket'
import Sort from './Sort'
import Post from '../Post/List'
import GridModal from '../Post/Gridmodal'
import state from '../state'
import mixin from '../tabs'
import ProjectInstructions from './ProjectInstructions'
import features from '@/mixins/features'
const sortTransition = 'slide'

export default {
  components: {
    ProjectInstructions,
    Loader,
    Post,
    InfiniteScroll,
    Sort,
    GridModal
  },
  mixins: [componentState, auth, mixin, features],
  props: ['showCreate', 'isSnapshot'],
  data: function () {
    return {
      subscribed: false,
      transitionName: '',
      showGridModal: false,
      selectedPostID: 0,
      scrollToComment: false,
      selectedCommentID: 0,
      isEditProjectInstructions: false,
      disableSaveButton: false
    }
  },
  computed: {
    isStory: function () {
      return this.$route.name === 'viewStory'
    },
    fromTemplate: function () {
      return !!this.$route.params.fromTemplate
    },
    fromStartButtonTemplate: function () {
      return !!this.$route.params.startButton || this.fromTemplate
    },
    showModal: function () {
      return this._get(state.grid.modalVisible) && this.selectedPostID !== 0
    },
    getCurrentUserProjectRole: function () {
      const memberData = this.$store.getters.membership(this._get(state.project.id), this.currentUser.uuid)
      return memberData ? memberData.role : ''
    },
    viewProjectInstructionPermission: function () {
      return !this.isSnapshot && (this.canViewInstructions || this.getCurrentUserProjectRole !== '' || this.project.isTemplate)
    },
    canViewInstructions: function () {
      return this.hasFeature('SeeReadonlyInstructions')
    },
    editPostInstructionsPermission: function () {
      return this.hasFeature('UpdateInstructionsOnTemplate') && this.project.isTemplate
    }
  },
  watch: {
    isSortedNewestFirst: function () {
      this.transitionName = sortTransition
      setTimeout(() => {
        this.resetTransitionName()
      }, 1000)
    },
    keyName: function (value) {
      this._setObjectKey(value)
    }
  },
  mounted: function () {
    if (!this.subscribed && this.project.id) {
      this.subscribed = true
      socket.subscribe('project', this.project.id, 'post')
      socket.subscribe('project', this.project.id, 'comment')
      socket.subscribe('project', this.project.id, 'attachment')
    }
  },
  created: function () {
    this.featureGroupName = 'Project View'
    this.getFeatures()
  },
  destroyed: function () {
    if (this.subscribed) {
      this.subscribed = false
      socket.unsubscribe('project', this.project.id, 'post')
      socket.unsubscribe('project', this.project.id, 'comment')
      socket.unsubscribe('project', this.project.id, 'attachment')
    }
  },
  methods: {
    sort: function () {
      if (!this.isSortedNewestFirst) {
        this._callback(state.posts.sort)
      }
    },
    resetTransitionName: function () {
      if (this.postLoading) {
        return setTimeout(() => { this.resetTransitionName() }, 1000)
      }
      this.transitionName = ''
    },
    checkCanSave: function () {
      let saveOk = true
      for (let i = 0; i < this.$refs.postRef.length; i++) {
        const postInEditMode = this.$refs.postRef[i].$refs.reply
        if (postInEditMode) {
          saveOk = saveOk && postInEditMode.checkCanSave()
        }
      }
      return saveOk
    },
    savePosts: function () {
      // eslint-disable-next-line no-async-promise-executor
      return new Promise(async (resolve) => {
        for (let i = 0; i < this.$refs.postRef.length; i++) {
          const post = this.$refs.postRef[i].$refs.reply
          if (post) {
            await post.saveNoDebounce()
          }
        }
        resolve()
      })
    },
    showPostGrid: function (value, value2) {
      this.selectedPostID = value
      this.selectedCommentID = value2
      this.scrollToComment = true
      this._set(state.grid.modalVisible, true)
    },
    onSetEditInstructions: function (value) {
      this.isEditProjectInstructions = value
    },
    onSetDisableSaveButton: function (value) {
      this.disableSaveButton = value
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~styles/utils.scss';

.create {
  padding: 1rem 2.5rem;
  padding-bottom: 0;
  width: 100%;
  max-width: 100%;
  @include mobile {
    padding: 1rem 0.75rem;
  }
}

.list {
  background-color: white;
  position: relative;
}

.loader-container {
  background: #f2f2f2;
  padding-top: 1rem;
  .loader {
    display: block;
    font-size: 2rem;
    margin: 1rem auto;
  }
}

.slide-enter-active {
animation: slide-in 1s ease-out forwards;
}

.slide-leave-active {
animation: slide-out 0.5s ease-out forwards;
}

@keyframes slide-in {
  from {
    transform: translateY(45vh);
    opacity:0;
  }
  to {
    transform: translateY(0);
    opacity:1;
  }
}

@keyframes slide-out {
  from {
    transform: translateY(0px);
    opacity:1;
  }
  to {
    transform: translateY(20vh);
    opacity:0;
  }
}

.slide-move{
  transition: transform 1s;
}

.isEng{
  background-color: #F8F8F8;
}

.project-instructions {
  color: $grey;
  padding: 0 1rem;
  font-size: 0.8rem;
  @include mobile {
    padding: 0.25rem 1rem 1rem 1rem;
  }
}

.sort {
  font-size: 8px;
}

.edit-instructions-button {
  font-size: 0.65rem;
  border-radius: 0;
  @include mobile {
    font-size: 0.6rem;
  }
}

.project-button-container {
  justify-content: flex-end;
  align-items: center;
  gap: 0.75rem;

  padding: 0.75rem;
  position: absolute;
  top: -80px;
  right: 0;
  @include mobile {
    justify-content: center;
    padding: 1rem 0;
    margin-right: 1rem;
  }
}
</style>
